import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Radar } from "lucide-react";
import { translateDomainName, translateStrengthName } from "@/lib/strength-i18n";

interface StrengthData {
  rank: number;
  name: string;
  nameAr?: string;
  domain: string;
  score?: number;
}

interface DomainScore {
  domain: string;
  count: number;
  percentage: number;
  color: string;
}

interface StrengthsChartProps {
  strengths: StrengthData[];
  showTop?: number; // Number of top strengths to display in bar chart
}

export default function StrengthsChart({ strengths, showTop = 10 }: StrengthsChartProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.split("-")[0] === "ar";

  const getDomainColor = (domain: string) => {
    switch (domain) {
      case "Strategic Thinking":
        return { bg: "bg-primary", text: "text-primary", color: "#1E7A8C" };
      case "Executing":
        return { bg: "bg-secondary", text: "text-brand-green-dark", color: "#5CB85C" };
      case "Influencing":
        return { bg: "bg-warning", text: "text-warning", color: "#F5A623" };
      case "Relationship Building":
        return { bg: "bg-brand-teal-dark", text: "text-brand-teal-dark", color: "#155E6D" };
      default:
        return { bg: "bg-secondary", text: "text-secondary", color: "hsl(0, 0%, 50%)" };
    }
  };

  // Calculate domain distribution
  const calculateDomainScores = (): DomainScore[] => {
    const domainCounts: Record<string, number> = {};
    
    strengths.forEach(strength => {
      domainCounts[strength.domain] = (domainCounts[strength.domain] || 0) + 1;
    });

    const total = strengths.length;
    return Object.entries(domainCounts).map(([domain, count]) => ({
      domain,
      count,
      percentage: (count / total) * 100,
      color: getDomainColor(domain).color,
    }));
  };

  const domainScores = calculateDomainScores();
  const topStrengths = strengths.slice(0, showTop);
  const maxCount = Math.max(...domainScores.map(d => d.count));

  return (
    <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
      <Tabs defaultValue="domains" className="w-full min-w-0">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="domains" className="gap-2">
            <Radar className="w-4 h-4" />
            {t('charts.domains')}
          </TabsTrigger>
          <TabsTrigger value="rankings" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            {t('charts.rankings')}
          </TabsTrigger>
        </TabsList>

        {/* Domain Distribution Chart */}
        <TabsContent value="domains" className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 sm:text-xl">{t('charts.domainDistribution')}</h3>
            <p className="text-sm text-muted-foreground mb-6">
              {t('charts.domainDistributionDesc')}
            </p>
          </div>

          {/* Radar-style visualization */}
          <div className="space-y-4">
            {domainScores.map((domainScore, index) => {
              const colors = getDomainColor(domainScore.domain);
              const barWidth = (domainScore.count / maxCount) * 100;

              return (
                <div key={index} className="space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                      <Badge
                        className="border"
                        style={{ backgroundColor: `${colors.color}15`, borderColor: `${colors.color}40`, color: colors.color }}
                      >
                        {translateDomainName(domainScore.domain, t, i18n.language)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {domainScore.count} {t('charts.strengths')}
                      </span>
                    </div>
                    <span className="text-sm font-medium">
                      {Math.round(domainScore.percentage)}%
                    </span>
                  </div>
                  <div className="relative h-8 bg-muted rounded-lg overflow-hidden">
                    <div
                      className={`h-full ${colors.bg} transition-all duration-500 flex items-center justify-end pr-3`}
                      style={{ width: `${barWidth}%` }}
                    >
                      <span className="text-xs font-medium text-white">
                        {domainScore.count}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-8 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm">
              <span className="font-semibold">{t('charts.dominantDomain')}:</span>{" "}
              <span style={{ color: domainScores[0]?.color }} className="font-semibold">
                {translateDomainName(domainScores[0]?.domain, t, i18n.language)}
              </span>
              {" "}({domainScores[0]?.count} {t('charts.strengths')})
            </p>
          </div>
        </TabsContent>

        {/* Rankings Bar Chart */}
        <TabsContent value="rankings" className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 sm:text-xl">
              {t('charts.topStrengthsRanking', { count: showTop })}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {t('charts.rankingDesc')}
            </p>
          </div>

          <div className="space-y-3">
            {topStrengths.map((strength, index) => {
              const colors = getDomainColor(strength.domain);
              const barWidth = ((showTop - index) / showTop) * 100;

              return (
                <div key={strength.rank} className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary">
                          #{strength.rank}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium">
                          {translateStrengthName(strength.name, t, i18n.language)}
                          {!isArabic && strength.nameAr && (
                            <span className="text-muted-foreground mr-2"> - {strength.nameAr}</span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {translateDomainName(strength.domain, t, i18n.language)}
                        </p>
                      </div>
                    </div>
                    {strength.score && (
                      <span className="text-sm font-medium">
                        {strength.score}%
                      </span>
                    )}
                  </div>
                  <div className="relative h-6 bg-muted rounded-lg overflow-hidden">
                    <div
                      className={`h-full ${colors.bg} transition-all duration-500`}
                      style={{ 
                        width: `${barWidth}%`,
                        opacity: 0.8,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {strengths.length > showTop && (
            <p className="text-sm text-muted-foreground text-center mt-6">
              {t('charts.showingTop', { count: showTop, total: strengths.length })}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </Card>
  );
}
